export type OrderStatus =
  | "pending"
  | "accepted"
  | "cooking"
  | "out for delivery"
  | "delivered";

export type Order = {
  id: number;
  name: string;
  fullAddress: string;
  contactNumber: string;
  deliveryCharge: string | number;
  vat: string | number;
  total: string | number;
  status: OrderStatus;
  notes?: string;
  createdAt: string;
};

export type OrderListResponse = {
  data: Order[];
  pagination: { total: number; limit: number; offset: number };
};

// normalize helper
const toNum = (v: any) =>
  typeof v === "string" ? parseFloat(v) : Number(v ?? 0);

function normalize(o: any): Order {
  return {
    id: Number(o?.id),
    name: String(o?.name ?? ""),
    fullAddress: String(o?.fullAddress ?? ""),
    contactNumber: String(o?.contactNumber ?? ""),
    deliveryCharge: toNum(o?.deliveryCharge),
    vat: toNum(o?.vat),
    total: toNum(o?.total),
    status: (o?.status ?? "pending") as OrderStatus,
    notes: o?.notes ?? "",
    createdAt: String(o?.createdAt ?? new Date().toISOString()),
  };
}

export async function listOrders(params: {
  status?: OrderStatus;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}): Promise<OrderListResponse> {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.startDate) qs.set("startDate", params.startDate);
  if (params.endDate) qs.set("endDate", params.endDate);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));

  const res = await fetch(`/api/order?${qs.toString()}`, {
    method: "GET",
    cache: "no-store",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`List orders failed (${res.status})`);
  const data = await res.json();

  const rows = Array.isArray(data?.data) ? data.data.map(normalize) : [];
  const pagination = data?.pagination ?? {
    total: rows.length,
    limit: params.limit ?? 10,
    offset: params.offset ?? 0,
  };

  return { data: rows, pagination };
}
export type OrderItem = {
  id: number; // line-item id (বা menuId fallback)
  menuId: number;
  menuName: string;
  quantity: number;
  price: number; // per-unit
  imageUrl?: string;
};

export type GetOrder = {
  id: number;
  name: string;
  fullAddress: string;
  contactNumber: string;
  deliveryCharge: number;
  vat: number;
  total: number;
  status: OrderStatus;
  notes?: string;
  createdAt: string;
  items: OrderItem[];
};
export async function getOrder(id: number): Promise<GetOrder> {
  const res = await fetch(`/api/order/${id}`, { cache: "no-store" });
  const j = await res.json();
  const o = j.order ?? j;

  return {
    id: Number(o.id),
    name: String(o.name ?? ""),
    fullAddress: String(o.fullAddress ?? ""),
    contactNumber: String(o.contactNumber ?? ""),
    deliveryCharge: Number(o.deliveryCharge ?? 0),
    vat: Number(o.vat ?? 0),
    total: Number(o.total ?? 0),
    status: (o.status as OrderStatus) ?? "pending",
    notes: o.notes ? String(o.notes) : undefined,
    createdAt: String(o.createdAt ?? new Date().toISOString()),

    items: Array.isArray(o.items)
      ? o.items.map((it: any) => ({
          id: Number(it.id ?? it.menuId ?? 0),
          menuId: Number(it.menuId ?? it.id ?? 0),
          menuName: String(it.menuName ?? it.name ?? ""),
          quantity: Number(it.quantity ?? 1),
          price: Number(it.price ?? 0),
          imageUrl: typeof it.imageUrl === "string" ? it.imageUrl : undefined,
        }))
      : [],
  };
}

// order.service.ts
export async function updateOrderStatus(
  id: number,
  status: OrderStatus
): Promise<Order> {
  const res = await fetch(`/api/order/status`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data: status, id: id }),
    credentials: "include",
  });

  if (!res.ok) throw new Error(`Update status failed (${res.status})`);
  const d = await res.json().catch(() => ({}));
  return normalize(d?.data ?? d);
}

/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError } from "axios";
import type { CartLine } from "@/lib/cart/types";

export type CreateOrderInput = {
  name: string;
  fullAddress: string;
  contactNumber: string;
  notes?: string;
  deliveryCharge: number; // e.g. 100
  vat: number; // absolute amount
  total: number; // absolute amount
  items: { menuId: number; quantity: number; price: number }[];
};

export type CreateOrder = {
  id: number;
  name: string;
  fullAddress: string;
  contactNumber: string;
  deliveryCharge: number | string;
  vat: number | string;
  total: number | string;
  status: "pending" | "accepted" | "cooking" | "out for delivery" | "delivered";
  notes?: string;
  createdAt: string;
  items: { menuId: number; quantity: number; price: number }[];
};

export async function createOrder(
  payload: CreateOrderInput
): Promise<CreateOrder> {
  try {
    const res = await axios.post("/api/place-order", payload);
    return (res.data?.order ?? res.data) as CreateOrder;
  } catch (err) {
    const e = err as AxiosError<{ message?: string; detail?: string }>;
    const msg =
      e.response?.data?.message || e.response?.data?.detail || e.message;
    throw new Error(msg || "Failed to place order");
  }
}

// helper: cart → payload (compute totals)
export function buildOrderPayloadFromCart(
  lines: CartLine[],
  buyer: {
    name: string;
    fullAddress: string;
    contactNumber: string;
    notes?: string;
  },
  opts?: { vatRatePct?: number; deliveryFlat?: number }
): CreateOrderInput {
  const selected = lines.filter((l) => l.selected);
  const subtotal = selected.reduce((s, l) => s + l.price * l.quantity, 0);
  const vat = (subtotal * (opts?.vatRatePct ?? 5)) / 100;
  const delivery = subtotal > 0 ? opts?.deliveryFlat ?? 100 : 0;
  const total = subtotal + vat + delivery;

  return {
    ...buyer,
    deliveryCharge: Number(delivery.toFixed(2)),
    vat: Number(vat.toFixed(2)),
    total: Number(total.toFixed(2)),
    items: selected.map((l) => ({
      menuId: l.id,
      quantity: l.quantity,
      price: Number(l.price), // unit price
    })),
  };
}
